import { ResponsiveActions } from "../../../components/layout";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Chip, CircularProgress, Slider, Stack, Typography } from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import StopOutlinedIcon from "@mui/icons-material/StopOutlined";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../../../widgets/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../widgets/Navbars/DashboardNavbar";
import {
  AppAutocomplete,
  AppButton,
  AppCheckbox,
  AppIconButton,
  AppTextField,
  AppToggleField,
} from "../../../components/form";
import { PageCommandBar, PageSectionCard } from "../../../components/layout";
import { AppFormField, AppFormGrid, PageListContainer } from "../../../components/Page";
import { GRID_FULL, GRID_HALF } from "../../../lib/ui/formLayout";
import JsonCodeEditor from "../../../components/JsonCodeEditor";
import OpenSearchIndexPicker from "../../../components/OpenSearchIndexPicker";
import IndexTimeFieldAutocomplete from "../../../components/IndexTimeFieldAutocomplete";
import WarehouseAnomalySourceCard from "./WarehouseAnomalySourceCard";
import { anomalyService } from "../../../services/anomalyService";
import type {
  AnomalyCriteriaDirection,
  AnomalyDefinition,
  AnomalyFeature,
  AnomalyPluginCriteria,
  AnomalyPluginDetectorStatus,
  AnomalySuppressionMode,
} from "../../../types/anomaly";

type Mode = "create" | "edit";

const BUCKET_INTERVAL_OPTIONS = ["5m", "10m", "15m", "30m", "1h", "6h", "1d"];
const LOOKBACK_OPTIONS = ["1h", "6h", "12h", "24h", "7d", "30d", "90d", "180d"];
const SCHEDULE_CRON_OPTIONS = [
  "0 */5 * * * *",
  "0 */15 * * * *",
  "0 */30 * * * *",
  "0 0 * * * *",
  "0 0 */6 * * *",
];

const defaultPluginCriteria = (): AnomalyPluginCriteria => ({
  direction: "ANY",
  customizeSuppression: true,
  suppressionMode: "PERCENT",
  suppressAboveExpected: 20,
  suppressBelowExpected: 20,
});

const emptyDefinition = (): AnomalyDefinition => ({
  name: "",
  description: "",
  enabled: true,
  indices: [],
  timeField: "",
  filterQuery: {},
  features: [{ name: "count", aggregation: "count" }],
  categoryField: "",
  bucketInterval: "10m",
  lookback: "24h",
  sensitivity: 0.5,
  scheduleCron: "0 */15 * * * *",
  backendOverride: "PLUGIN",
  detectionMode: "TREND",
  pluginCriteria: defaultPluginCriteria(),
});

function isValidJson(text: string): boolean {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}

function withCurrentOption(options: string[], current?: string | null): string[] {
  const trimmed = current?.trim();
  if (!trimmed || options.includes(trimmed)) return options;
  return [trimmed, ...options];
}

type DefinitionEditPageProps = {
  mode: Mode;
  /** When true, render form only (for AppSlideOver / EditorSlideOver hosts). */
  embedded?: boolean;
  /** Edit id when embedded (route params are unused in slide-over hosts). */
  definitionId?: string;
  onClose?: () => void;
  onSaved?: () => void;
};

export default function DefinitionEditPage({
  mode,
  embedded = false,
  definitionId,
  onClose,
  onSaved,
}: DefinitionEditPageProps) {
  const { id: routeId } = useParams();
  const id = definitionId ?? routeId;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const goBack = () => {
    if (onClose) onClose();
    else navigate("/anomalies");
  };
  const [def, setDef] = useState<AnomalyDefinition>(emptyDefinition);
  const [filterText, setFilterText] = useState("{}");
  const [featuresText, setFeaturesText] = useState(
    JSON.stringify([{ name: "count", aggregation: "count" }], null, 2)
  );
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backendDetail, setBackendDetail] = useState<string | null>(null);
  const [pluginAvailable, setPluginAvailable] = useState(false);
  const [detectorStatus, setDetectorStatus] = useState<AnomalyPluginDetectorStatus | null>(null);
  const [detectorBusy, setDetectorBusy] = useState(false);

  const pageTitle = mode === "create" ? t("anomaly.create") : t("anomaly.edit");
  const primaryIndex = def.indices?.[0] ?? "";

  const bucketOptions = useMemo(
    () => withCurrentOption(BUCKET_INTERVAL_OPTIONS, def.bucketInterval),
    [def.bucketInterval]
  );
  const lookbackOptions = useMemo(
    () => withCurrentOption(LOOKBACK_OPTIONS, def.lookback),
    [def.lookback]
  );
  const cronOptions = useMemo(
    () => withCurrentOption(SCHEDULE_CRON_OPTIONS, def.scheduleCron),
    [def.scheduleCron]
  );

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const loaded = await anomalyService.getDefinition(id);
      setDef({
        ...emptyDefinition(),
        ...loaded,
        backendOverride: "PLUGIN",
        detectionMode: "TREND",
        pluginCriteria: {
          ...defaultPluginCriteria(),
          ...(loaded.pluginCriteria || {}),
        },
      });
      setFilterText(JSON.stringify(loaded.filterQuery ?? {}, null, 2));
      setFeaturesText(JSON.stringify(loaded.features ?? [], null, 2));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    anomalyService
      .backendStatus()
      .then((s) => {
        setBackendDetail(s.detail || s.effectiveDefault);
        setPluginAvailable(Boolean(s.pluginAvailable));
      })
      .catch(() => {
        setBackendDetail(null);
        setPluginAvailable(false);
      });
  }, []);

  // PLUGIN-only definitions: capable whenever the AD plugin is available.
  const pluginCapable = mode === "edit" && Boolean(id) && pluginAvailable;

  const refreshDetectorStatus = useCallback(async () => {
    if (!id || !pluginCapable) {
      setDetectorStatus(null);
      return;
    }
    try {
      const status = await anomalyService.pluginDetectorStatus(id);
      setDetectorStatus(status);
      if (status.pluginDetectorId) {
        setDef((d) =>
          d.pluginDetectorId === status.pluginDetectorId
            ? d
            : { ...d, pluginDetectorId: status.pluginDetectorId }
        );
      }
    } catch {
      setDetectorStatus(null);
    }
  }, [id, pluginCapable]);

  useEffect(() => {
    void refreshDetectorStatus();
  }, [refreshDetectorStatus]);

  useEffect(() => {
    const s = detectorStatus?.state?.toUpperCase();
    if (s !== "INITIALIZING" && s !== "INIT") return;
    const timer = window.setInterval(() => {
      void refreshDetectorStatus();
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [detectorStatus?.state, refreshDetectorStatus]);

  const filterInvalid = useMemo(() => !isValidJson(filterText), [filterText]);
  const featuresInvalid = useMemo(() => !isValidJson(featuresText), [featuresText]);
  const criteria = def.pluginCriteria || defaultPluginCriteria();
  const direction = criteria.direction || "ANY";
  const showAbove =
    criteria.customizeSuppression !== false &&
    (direction === "ANY" || direction === "RISE");
  const showBelow =
    criteria.customizeSuppression !== false &&
    (direction === "ANY" || direction === "DROP");

  const setPluginCriteria = (patch: Partial<AnomalyPluginCriteria>) => {
    setDef((d) => ({
      ...d,
      pluginCriteria: {
        ...defaultPluginCriteria(),
        ...(d.pluginCriteria || {}),
        ...patch,
      },
    }));
  };

  const isWarehouse = def.sourceType === "WAREHOUSE";

  const buildPayload = (): AnomalyDefinition => {
    if (isWarehouse) {
      return {
        ...def,
        detectionMode: "TREND",
        indices: [],
        features: [],
        filterQuery: undefined,
        pluginCriteria: undefined,
      };
    }
    const features = JSON.parse(featuresText) as AnomalyFeature[];
    const filterQuery = JSON.parse(filterText) as Record<string, unknown>;
    return {
      ...def,
      detectionMode: "TREND",
      backendOverride: "PLUGIN",
      indices: def.indices || [],
      features,
      filterQuery,
      categoryField: def.categoryField?.trim() || undefined,
      pluginCriteria: def.pluginCriteria || defaultPluginCriteria(),
    };
  };

  const handleSave = async () => {
    setError(null);
    if (!isWarehouse && (filterInvalid || featuresInvalid)) {
      setError(t("anomaly.invalidJson"));
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (mode === "create") {
        const created = await anomalyService.createDefinition(payload);
        if (embedded) {
          onSaved?.();
          onClose?.();
        } else {
          navigate(`/anomalies/${created.id}/edit`, { replace: true });
        }
      } else if (id) {
        await anomalyService.updateDefinition(id, payload);
        await load();
        if (embedded) onSaved?.();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (!id) return;
    setRunning(true);
    setError(null);
    try {
      await anomalyService.runNow(id);
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("anomaly.runNow"),
            content: t("anomaly.runStarted"),
          },
        })
      );
      await refreshDetectorStatus();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  const handleStartDetector = async () => {
    if (!id) return;
    setDetectorBusy(true);
    setError(null);
    try {
      const status = await anomalyService.startPluginDetector(id);
      setDetectorStatus(status);
      if (status.pluginDetectorId) {
        setDef((d) => ({ ...d, pluginDetectorId: status.pluginDetectorId }));
      }
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("anomaly.pluginDetectorStart"),
            content: t("anomaly.pluginDetectorStarted"),
          },
        })
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      await refreshDetectorStatus();
    } finally {
      setDetectorBusy(false);
    }
  };

  const handleStopDetector = async () => {
    if (!id) return;
    setDetectorBusy(true);
    setError(null);
    try {
      const status = await anomalyService.stopPluginDetector(id);
      setDetectorStatus(status);
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("anomaly.pluginDetectorStop"),
            content: t("anomaly.pluginDetectorStopped"),
          },
        })
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      await refreshDetectorStatus();
    } finally {
      setDetectorBusy(false);
    }
  };

  const shell = (body: ReactNode) =>
    embedded ? (
      <>{body}</>
    ) : (
      <DashboardLayout>
        <DashboardNavbar />
        {body}
      </DashboardLayout>
    );

  if (loading) {
    return shell(
      <PageListContainer>
        <CircularProgress size={28} />
      </PageListContainer>
    );
  }

  return shell(
      <PageListContainer>
        <PageCommandBar
          leading={
            <AppIconButton
              title={t("common.back", { defaultValue: "Back" })}
              onClick={goBack}
            >
              <ArrowBackOutlinedIcon fontSize="small" />
            </AppIconButton>
          }
          title={pageTitle}
          subtitle={
            pluginCapable ? (
              <Chip
                size="small"
                color={
                  detectorStatus?.state === "INITIALIZING" || detectorStatus?.state === "INIT"
                    ? "info"
                    : detectorStatus?.state === "RUNNING"
                      ? "success"
                      : "default"
                }
                label={`${t("anomaly.pluginDetectorState")}: ${detectorStatus?.state || "…"}`}
              />
            ) : undefined
          }
          actions={
            <ResponsiveActions
              items={[
                {
                  key: "run",
                  label: t("anomaly.runNow"),
                  icon: "play_arrow",
                  onClick: () => void handleRunNow(),
                  disabled: running,
                  hidden: mode !== "edit",
                },
                {
                  key: "start",
                  label: t("anomaly.pluginDetectorStart"),
                  icon: "play_circle",
                  color: "success",
                  onClick: () => void handleStartDetector(),
                  disabled:
                    detectorBusy ||
                    detectorStatus?.state === "RUNNING" ||
                    detectorStatus?.state === "INITIALIZING" ||
                    detectorStatus?.state === "INIT",
                  hidden: !pluginCapable,
                },
                {
                  key: "stop",
                  label: t("anomaly.pluginDetectorStop"),
                  icon: "stop_circle",
                  color: "warning",
                  onClick: () => void handleStopDetector(),
                  disabled:
                    detectorBusy ||
                    !(
                      detectorStatus?.state === "RUNNING" ||
                      detectorStatus?.state === "INITIALIZING" ||
                      detectorStatus?.state === "INIT"
                    ),
                  hidden: !pluginCapable,
                },
                {
                  key: "save",
                  label: saving ? t("common.saving") : t("common.save"),
                  onClick: () => void handleSave(),
                  disabled: saving,
                  primary: true,
                },
              ]}
            />
          }
        />

        {backendDetail && (
          <Alert severity="info">
            {backendDetail}
          </Alert>
        )}
        {pluginCapable && (
          <Alert
            severity={
              detectorStatus?.state === "INITIALIZING" || detectorStatus?.state === "INIT"
                ? "info"
                : detectorStatus?.state === "RUNNING"
                  ? "success"
                  : "info"
            }
            sx={{ mb: 2 }}
            action={
              <Stack direction="row" spacing={1}>
                <AppButton
                  variant="outlined"
                  color="success"
                  size="small"
                  startIcon={<PlayArrowOutlinedIcon />}
                  onClick={() => void handleStartDetector()}
                  disabled={
                    detectorBusy ||
                    detectorStatus?.state === "RUNNING" ||
                    detectorStatus?.state === "INITIALIZING" ||
                    detectorStatus?.state === "INIT"
                  }
                >
                  {t("anomaly.pluginDetectorStart")}
                </AppButton>
                <AppButton
                  variant="outlined"
                  color="warning"
                  size="small"
                  startIcon={<StopOutlinedIcon />}
                  onClick={() => void handleStopDetector()}
                  disabled={
                    detectorBusy ||
                    !(
                      detectorStatus?.state === "RUNNING" ||
                      detectorStatus?.state === "INITIALIZING" ||
                      detectorStatus?.state === "INIT"
                    )
                  }
                >
                  {t("anomaly.pluginDetectorStop")}
                </AppButton>
              </Stack>
            }
          >
            <Typography variant="subtitle2">{t("anomaly.pluginDetector")}</Typography>
            <Typography variant="body2">
              {t("anomaly.pluginDetectorState")}: <strong>{detectorStatus?.state || "…"}</strong>
              {detectorStatus?.pluginDetectorId
                ? ` · ${t("anomaly.pluginDetectorId")}: ${detectorStatus.pluginDetectorId}`
                : ""}
            </Typography>
            {(detectorStatus?.state === "INITIALIZING" || detectorStatus?.state === "INIT") && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {t("anomaly.pluginDetectorInitializingHelp")}
              </Typography>
            )}
            {detectorStatus?.detail && (
              <Typography variant="caption" display="block" color="text.secondary">
                {detectorStatus.detail}
              </Typography>
            )}
          </Alert>
        )}
        {error && (
          <Alert severity="error">
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          <PageSectionCard title={t("anomaly.definition")}>
            <AppFormGrid>
              <AppFormField size={GRID_HALF}>
                <AppTextField
                  label={t("anomaly.name")}
                  fullWidth
                  value={def.name || ""}
                  onChange={(e) => setDef((d) => ({ ...d, name: e.target.value }))}
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppCheckbox
                  label={t("anomaly.enabled")}
                  checked={Boolean(def.enabled)}
                  onChange={(e) => setDef((d) => ({ ...d, enabled: e.target.checked }))}
                />
              </AppFormField>
              <AppFormField size={GRID_HALF}>
                <AppAutocomplete<"OPENSEARCH" | "WAREHOUSE">
                  label={t("anomaly.warehouse.source")}
                  options={["OPENSEARCH", "WAREHOUSE"]}
                  value={(def.sourceType || "OPENSEARCH") as "OPENSEARCH" | "WAREHOUSE"}
                  disableClearable
                  disabled={mode === "edit"}
                  getOptionLabel={(sourceType) =>
                    sourceType === "WAREHOUSE"
                      ? t("anomaly.warehouse.sourceWarehouse")
                      : t("anomaly.warehouse.sourceOpenSearch")
                  }
                  onChange={(_, sourceType) => {
                    if (!sourceType) return;
                    setDef((d) => ({
                      ...d,
                      sourceType,
                      bucketInterval: sourceType === "WAREHOUSE" ? "1d" : "10m",
                      lookback: sourceType === "WAREHOUSE" ? "30d" : "24h",
                      scheduleCron: sourceType === "WAREHOUSE" ? "0 0 * * * *" : d.scheduleCron,
                    }));
                  }}
                />
              </AppFormField>
              <AppFormField size={GRID_FULL}>
                <AppTextField
                  label={t("anomaly.description")}
                  fullWidth
                  multiline
                  minRows={2}
                  value={def.description || ""}
                  onChange={(e) => setDef((d) => ({ ...d, description: e.target.value }))}
                />
              </AppFormField>
            </AppFormGrid>
          </PageSectionCard>

          {isWarehouse ? (
            <WarehouseAnomalySourceCard
              def={def}
              onChange={(patch) => setDef((d) => ({ ...d, ...patch }))}
              lookbackOptions={lookbackOptions}
              cronOptions={cronOptions}
            />
          ) : (
            <>
          <AppFormGrid>
            <AppFormField size={GRID_HALF}>
              <PageSectionCard
                fillHeight
                title={t("anomaly.indices")}
                description={t("anomaly.indicesHelp")}
              >
                <AppFormGrid>
                  <AppFormField size={GRID_FULL}>
                    <OpenSearchIndexPicker
                      label={t("anomaly.indices")}
                      helperText={t("anomaly.indicesHelp")}
                      value={def.indices || []}
                      onChange={(indices) => setDef((d) => ({ ...d, indices }))}
                      fullWidth
                    />
                  </AppFormField>
                  <AppFormField size={GRID_HALF}>
                    <IndexTimeFieldAutocomplete
                      index={primaryIndex}
                      label={t("anomaly.timeField")}
                      value={def.timeField}
                      onChange={(timeField) => setDef((d) => ({ ...d, timeField }))}
                      showReload
                    />
                  </AppFormField>
                  <AppFormField size={GRID_HALF}>
                    <AppTextField
                      label={t("anomaly.categoryField")}
                      fullWidth
                      value={def.categoryField || ""}
                      onChange={(e) => setDef((d) => ({ ...d, categoryField: e.target.value }))}
                    />
                  </AppFormField>
                  <AppFormField size={GRID_HALF}>
                    <AppAutocomplete<string>
                      label={t("anomaly.bucketInterval")}
                      helperText={t("anomaly.bucketIntervalHelp")}
                      options={bucketOptions}
                      value={def.bucketInterval || null}
                      onChange={(_, value) => setDef((d) => ({ ...d, bucketInterval: value || "" }))}
                      disableClearable={Boolean(def.bucketInterval)}
                    />
                  </AppFormField>
                  <AppFormField size={GRID_HALF}>
                    <AppAutocomplete<string>
                      label={t("anomaly.lookback")}
                      helperText={t("anomaly.lookbackHelp")}
                      options={lookbackOptions}
                      value={def.lookback || null}
                      onChange={(_, value) => setDef((d) => ({ ...d, lookback: value || "" }))}
                      disableClearable={Boolean(def.lookback)}
                    />
                  </AppFormField>
                  <AppFormField size={GRID_FULL}>
                    <AppAutocomplete<string>
                      label={t("anomaly.scheduleCron")}
                      helperText={t("anomaly.scheduleCronHelp")}
                      options={cronOptions}
                      value={def.scheduleCron || null}
                      onChange={(_, value) => setDef((d) => ({ ...d, scheduleCron: value || "" }))}
                    />
                  </AppFormField>
                </AppFormGrid>
              </PageSectionCard>
            </AppFormField>

            <AppFormField size={GRID_HALF}>
              <PageSectionCard
                fillHeight
                title={t("anomaly.pluginCriteria")}
                description={t("anomaly.detectionModeHelp")}
              >
                <AppFormGrid>
                  <AppFormField size={GRID_HALF}>
                    <AppToggleField
                      label={t("anomaly.criteriaDirection")}
                      value={direction}
                      onChange={(value) =>
                        setPluginCriteria({ direction: value as AnomalyCriteriaDirection })
                      }
                      options={[
                        { value: "ANY", label: t("anomaly.criteriaAny") },
                        { value: "RISE", label: t("anomaly.criteriaRise") },
                        { value: "DROP", label: t("anomaly.criteriaDrop") },
                      ]}
                    />
                  </AppFormField>
                  <AppFormField size={GRID_HALF}>
                    <AppCheckbox
                      label={t("anomaly.customizeSuppression")}
                      checked={criteria.customizeSuppression !== false}
                      onChange={(e) => setPluginCriteria({ customizeSuppression: e.target.checked })}
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t("anomaly.customizeSuppressionHelp")}
                    </Typography>
                  </AppFormField>
                  {criteria.customizeSuppression !== false && (
                    <>
                      <AppFormField size={GRID_HALF}>
                        <AppToggleField
                          label={t("anomaly.suppressionMode")}
                          value={criteria.suppressionMode || "PERCENT"}
                          onChange={(value) =>
                            setPluginCriteria({
                              suppressionMode: value as AnomalySuppressionMode,
                            })
                          }
                          options={[
                            { value: "PERCENT", label: t("anomaly.suppressionPercent") },
                            { value: "UNITS", label: t("anomaly.suppressionUnits") },
                          ]}
                        />
                      </AppFormField>
                      {showAbove && (
                        <AppFormField size={GRID_HALF}>
                          <AppTextField
                            type="number"
                            label={t("anomaly.suppressAbove")}
                            fullWidth
                            inputProps={{ min: 0, step: 1 }}
                            value={criteria.suppressAboveExpected ?? ""}
                            onChange={(e) => {
                              const raw = e.target.value;
                              setPluginCriteria({
                                suppressAboveExpected: raw === "" ? null : Number(raw),
                              });
                            }}
                            helperText={
                              (criteria.suppressionMode || "PERCENT") === "PERCENT"
                                ? "%"
                                : t("anomaly.suppressionUnits")
                            }
                          />
                        </AppFormField>
                      )}
                      {showBelow && (
                        <AppFormField size={GRID_HALF}>
                          <AppTextField
                            type="number"
                            label={t("anomaly.suppressBelow")}
                            fullWidth
                            inputProps={{ min: 0, step: 1 }}
                            value={criteria.suppressBelowExpected ?? ""}
                            onChange={(e) => {
                              const raw = e.target.value;
                              setPluginCriteria({
                                suppressBelowExpected: raw === "" ? null : Number(raw),
                              });
                            }}
                            helperText={
                              (criteria.suppressionMode || "PERCENT") === "PERCENT"
                                ? "%"
                                : t("anomaly.suppressionUnits")
                            }
                          />
                        </AppFormField>
                      )}
                    </>
                  )}
                  <AppFormField size={GRID_FULL}>
                    <Typography variant="caption" color="text.secondary">
                      {t("anomaly.sensitivity")}: {(def.sensitivity ?? 0.5).toFixed(2)}
                    </Typography>
                    <Slider
                      min={0}
                      max={1}
                      step={0.05}
                      value={def.sensitivity ?? 0.5}
                      onChange={(_, v) => setDef((d) => ({ ...d, sensitivity: v as number }))}
                      valueLabelDisplay="auto"
                    />
                  </AppFormField>
                </AppFormGrid>
              </PageSectionCard>
            </AppFormField>
          </AppFormGrid>

          <PageSectionCard title={t("anomaly.features")} description={t("anomaly.featuresHelp")}>
            <JsonCodeEditor
              label={t("anomaly.features")}
              helperText={t("anomaly.featuresHelp")}
              value={featuresText}
              onChange={setFeaturesText}
              height={160}
              error={featuresInvalid}
            />
          </PageSectionCard>

          <PageSectionCard title={t("anomaly.filterQuery")} description={t("anomaly.filterHelp")}>
            <JsonCodeEditor
              label={t("anomaly.filterQuery")}
              helperText={t("anomaly.filterHelp")}
              value={filterText}
              onChange={setFilterText}
              height={180}
              error={filterInvalid}
            />
          </PageSectionCard>
            </>
          )}
        </Stack>
      </PageListContainer>
  );
}
